import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedDimension } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedComparisonGroup } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedEmployeeGroup } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import React, { useMemo } from 'react';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { SearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

type GetSelectedDimensionLevelProps = {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  databaseRevisionId: string;
  databaseId: string;
  organizationId: string;
};

type ComparisonGroupMenuProps = GetSelectedDimensionLevelProps & {
  getAllLevelsForDimension: UseDimensionConfigPayload['getAllLevelsForDimension'];
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
};

/**
 * Get the selected dimension and level from other filters. If the employee
 * group is selected, return the dimension and level of the employee group.
 * If the dimension is selected, return the dimension and an empty level.
 */
const getSelectedDimensionLevel = ({
  store,
  feature,
  focusArea,
  analysisName,
  databaseRevisionId,
  databaseId,
  organizationId,
}: GetSelectedDimensionLevelProps) => {
  const currentDimensionLevel = getSelectedEmployeeGroup({
    focusArea,
    analysisName,
    store,
    feature,
    databaseId,
    databaseRevisionId,
    organizationId,
  });

  if (currentDimensionLevel !== null) {
    return [currentDimensionLevel.dimension, currentDimensionLevel.level];
  }

  const selectedDimension = getSelectedDimension({
    feature,
    organizationId,
    databaseRevisionId,
    databaseId,
    store,
    focusArea,
    analysisName,
  });

  if (selectedDimension !== null) {
    return [selectedDimension, ''];
  }

  return null;
};

export const ComparisonGroupMenu = (props: ComparisonGroupMenuProps) => {
  const {
    getAllLevelsForDimension,
    store,
    feature,
    focusArea,
    analysisName,
    databaseRevisionId,
    databaseId,
    organizationId,
    closeMenu,
    setPrimaryFilters,
  } = props;

  const language = useLanguage();
  const items = useMemo(() => {
    const currentDimensionLevel = getSelectedDimensionLevel(props);

    if (!currentDimensionLevel) {
      return [];
    }

    const [currentDimension, currentLevel] = currentDimensionLevel;

    const currentComparisonGroup = getSelectedComparisonGroup({
      feature,
      focusArea,
      analysisName,
      databaseRevisionId,
      databaseId,
      organizationId,
      store,
    });

    const levels = getAllLevelsForDimension(currentDimension);

    return levels
      .filter((l) => l.columnValue !== currentLevel)
      .map((level) => ({
        value: level.columnValue,
        translatedContent: language.levelDisplayName({
          definition: level,
        }),
        selected: level.columnValue === currentComparisonGroup,
      }));
  }, [
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    getAllLevelsForDimension,
    organizationId,
    props,
    store,
    language,
  ]);

  return (
    <FilterMenuSinglePanel
      filterNameTranslatedContent={language.comparisonGroup()}
      filterContent={
        <SearchableFilterListBox
          items={items}
          multiple={false}
          onChange={(newValues) => {
            if (newValues.length > 0) {
              setPrimaryFilters({
                selections: [
                  {
                    primaryFilterKey: 'comparisonGroup',
                    value: newValues[0],
                  },
                ],
              });
            }

            closeMenu();
          }}
        />
      }
      bottomProps={'none'}
    />
  );
};
